import * as React from 'react';
import { Button } from '@wayste/shadcn/components/button';
import { cn } from '@wayste/shadcn/lib/utils';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Info, X } from 'lucide-react';

function TooltipProvider({ delayDuration = 0, ...props }: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
    return <TooltipPrimitive.Provider data-slot="tooltip-provider" delayDuration={delayDuration} {...props} />;
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
    return (
        <TooltipProvider>
            <TooltipPrimitive.Root data-slot="tooltip" {...props} />
        </TooltipProvider>
    );
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
    return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

interface TooltipContentProps extends React.ComponentProps<typeof TooltipPrimitive.Content> {
    variant?: 'light' | 'dark';
}

function TooltipContent({ className, sideOffset = 4, variant = 'light', children, ...props }: TooltipContentProps) {
    const isDark = variant === 'dark';

    return (
        <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
                data-slot="tooltip-content"
                sideOffset={sideOffset}
                className={cn(
                    'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-(--radix-tooltip-content-transform-origin) z-[500] w-auto max-w-[320px] text-balance rounded-lg p-3 text-xs shadow-[0px_1px_4px_1px_rgba(0,0,0,0.1)]',
                    isDark ? 'bg-surface-inverse text-text-inverse' : 'bg-surface-tertiary text-text-primary',
                    className,
                )}
                {...props}
            >
                {children}
                <TooltipPrimitive.Arrow
                    className={cn(
                        'z-[500] size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]',
                        isDark ? 'bg-surface-inverse fill-surface-inverse' : 'bg-surface-tertiary fill-surface-tertiary',
                    )}
                />
            </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
    );
}

interface InfoTooltipProps extends Omit<React.ComponentProps<'button'>, 'children'> {
    title?: string;
    description?: string;
    showCloseButton?: boolean;
    disabled?: boolean;
    className?: string;
    side?: 'top' | 'right' | 'bottom' | 'left';
    sideOffset?: number;
    variant?: 'light' | 'dark';
}

function InfoTooltip({
    title,
    description,
    showCloseButton = false,
    disabled,
    className,
    side = 'top',
    sideOffset = 4,
    variant = 'light',
    ...props
}: InfoTooltipProps) {
    const [open, setOpen] = React.useState<boolean | undefined>(false);

    const tooltipProps = showCloseButton ? { open, onOpenChange: setOpen } : {};

    return (
        <Tooltip {...tooltipProps}>
            <TooltipTrigger asChild>
                <Button
                    type="button"
                    className={cn(
                        'text-text-secondary hover:text-text-primary focus:ring-border-focus focus:outline-none focus:ring-2 focus:ring-offset-1',
                        disabled && 'cursor-not-allowed opacity-50',
                        className,
                    )}
                    disabled={disabled}
                    tabIndex={-1}
                    variant="ghost"
                    aria-label={title || description || undefined}
                    size="icon"
                    {...props}
                >
                    <Info className="size-4" />
                </Button>
            </TooltipTrigger>
            <TooltipContent side={side} sideOffset={sideOffset} variant={variant}>
                <div className="flex items-start gap-3">
                    <div className="flex flex-1 flex-col gap-2">
                        {title && (
                            <p
                                className={cn(
                                    'text-sm font-semibold leading-5',
                                    variant === 'dark' ? 'text-[var(--text-inverse)]' : 'text-[var(--text-primary)]',
                                )}
                            >
                                {title}
                            </p>
                        )}
                        {description && (
                            <p
                                className={cn(
                                    'text-xs leading-4',
                                    variant === 'dark' ? 'text-[var(--text-inverse)]' : 'text-[var(--text-secondary)]',
                                )}
                            >
                                {description}
                            </p>
                        )}
                    </div>
                    {showCloseButton && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => setOpen(false)}
                            className="text-text-secondary hover:text-text-primary"
                            aria-label="Close tooltip"
                        >
                            <X className="size-4" />
                        </Button>
                    )}
                </div>
            </TooltipContent>
        </Tooltip>
    );
}

export { TooltipTrigger, TooltipContent, TooltipProvider, Tooltip, InfoTooltip };
